import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { getAllBotsData, getVendorId } from '@/services/bots';
import {
  MessageSquare,
  BookOpen,
  Palette,
  MousePointerClick,
  Brain,
  Settings,
  UserCircle,
  Users,
  CalendarDays,
  Plus,
} from 'lucide-react';
import { FC, Fragment, useEffect, useState, startTransition, useMemo, useCallback } from 'react';
import { useSearchParams } from 'next/navigation';
import NewBot from '../bot/components/NewBot';
import Customize from './components/Customize';
import Messages from './components/Messages';
import PageCTAs from './components/page-ctas/PageCTAs';
import Persona from './components/Persona';
import Setting from './components/Setting';
import HumanTransfer from './components/HumanTransfer';
import DemoBooking from './components/DemoBooking';
import AvatarSettings from './components/AvatarSettings';
import KnowledgeHub from './components/KnowledgeHub';
import { FilterSelect, type FilterSelectOption } from '@/components/shared/Filters';
import { PageHeader } from '@/components/shared/PageHeader';
import { Skeleton } from '@/components/ui/skeleton';

enum AGENT_TAB {
  MESSAGES = 'messages',
  PERSONA = 'persona',
  KNOWLEDGE = 'knowledge',
  CUSTOMIZE = 'customize',
  AVATAR = 'avatar',
  PAGE_CTAS = 'page_ctas',
  HUMAN_TRANSFER = 'human_transfer',
  DEMO_BOOKING = 'demo_booking',
  SETTINGS = 'settings',
}

// Valid ?tab= values, used to validate the query param before applying it.
const TAB_VALUES: string[] = Object.values(AGENT_TAB);

// Tabs that manage their own internal scroll + fixed footer (or full-height sub-layout).
// These get height passed all the way down (like MESSAGES) so the panel fills the card.
// They also skip the shared p-6 wrapper — each component owns its own padding.
const FILL_TABS = [
  AGENT_TAB.MESSAGES,
  AGENT_TAB.PERSONA,
  AGENT_TAB.CUSTOMIZE,
  AGENT_TAB.AVATAR,
  AGENT_TAB.PAGE_CTAS,
  AGENT_TAB.HUMAN_TRANSFER,
  AGENT_TAB.DEMO_BOOKING,
  AGENT_TAB.SETTINGS,
] as const;

type BotOption = { label: string; value: { accesstoken: string; customizationdata?: { name?: string }; status: boolean } };

const CustomLivePage: FC = () => {
  const searchParams = useSearchParams();

  // Initialize from ?tab= so deep links / refreshes land on the right tab (no flash).
  const [activeTab, setActiveTab] = useState<string>(() => {
    const t = searchParams.get('tab');
    return t && TAB_VALUES.includes(t) ? t : AGENT_TAB.MESSAGES;
  });

  // state -> URL: write ?tab= into the address bar without triggering a Next.js
  // navigation cycle. router.replace re-renders server components which is unnecessary
  // for this purely client-side tab switch.
  const selectTab = useCallback((value: string) => {
    setActiveTab(value);
    const url = new URL(window.location.href);
    url.searchParams.set('tab', value);
    window.history.replaceState(null, '', url.toString());
  }, []);

  // URL -> state: sync on browser back/forward (popstate fires on history.go etc.)
  // and on server-side navigations that land with a ?tab= param.
  useEffect(() => {
    const onPopState = () => {
      const t = new URLSearchParams(window.location.search).get('tab');
      if (t && TAB_VALUES.includes(t)) setActiveTab(t);
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);
  const [bot, setBot] = useState<BotOption | null>(null);
  const [options, setOptions] = useState<BotOption[]>([]);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [primaryColor, setPrimaryColor] = useState<string>('#3662E3');
  // True until the first bot load resolves, so we render a skeleton instead of
  // flashing the "Create Your First Bot" empty state while data is in flight.
  const [initializing, setInitializing] = useState(true);

  const handleBotSelect = (value: string) => {
    const index = Number(value);
    const selectedBot = options[index];
    if (!selectedBot) return;
    setBot(selectedBot);
    localStorage.setItem('botToken', selectedBot.value.accesstoken);
  };

  const loadBots = useCallback(async (preserveToken?: string) => {
    const vendorConfig = await getVendorId();
    const vendorId: number = vendorConfig?.vendorId as number;

    if (!vendorId) {
      throw new Error('Failed to retrieve vendor ID');
    }

    const botOptions: Array<{ status: boolean; customizationdata?: { name?: string }; accesstoken: string }> | null =
      await getAllBotsData(vendorId);

    if (botOptions !== null) {
      const arr: BotOption[] = [];
      for (const item of botOptions) {
        if (item.status === true && item.accesstoken) {
          arr.push({
            label: item.customizationdata?.name || 'Unnamed Bot',
            value: {
              accesstoken: item.accesstoken,
              customizationdata: item.customizationdata,
              status: item.status,
            },
          });
        }
      }

      const tokenToSelect = preserveToken ?? localStorage.getItem('botToken') ?? undefined;
      const found = tokenToSelect ? arr.find((item) => item.value.accesstoken === tokenToSelect) : null;
      const selected = found ?? arr[0] ?? null;

      startTransition(() => {
        setOptions(arr);
        setBot(selected);
        if (selected) {
          localStorage.setItem('botToken', selected.value.accesstoken);
        }
      });
    } else {
      startTransition(() => {
        setOptions([]);
        setBot(null);
      });
    }
  }, []);

  useEffect(() => {
    loadBots()
      .catch(() => {})
      .finally(() => setInitializing(false));
  }, [loadBots]);

  // After a delete the remembered token points at nothing; drop it so the reload
  // selects the first remaining bot, or shows the create state when none is left.
  const handleBotDeleted = useCallback(() => {
    localStorage.removeItem('botToken');
    loadBots().catch(() => {});
  }, [loadBots]);

  const selectedBotValue = (() => {
    if (!bot) return '';
    const index = options.findIndex((item) => item.value.accesstoken === bot.value.accesstoken);
    return index >= 0 ? String(index) : '';
  })();

  const AGENT_TABS = useMemo(
    () => [
      {
        label: 'Messages',
        icon: MessageSquare,
        value: AGENT_TAB.MESSAGES,
        group: 'Conversations',
        needsDynamicProps: true,
        component: null,
      },
      {
        label: 'AI & Persona',
        icon: Brain,
        value: AGENT_TAB.PERSONA,
        group: 'Intelligence',
        component: <Persona token={bot?.value?.accesstoken || ''} />,
      },
      {
        label: 'Knowledge Base',
        icon: BookOpen,
        value: AGENT_TAB.KNOWLEDGE,
        group: 'Intelligence',
        component: <KnowledgeHub token={bot?.value?.accesstoken || ''} />,
      },
      {
        label: 'Appearance',
        icon: Palette,
        value: AGENT_TAB.CUSTOMIZE,
        group: 'Design',
        needsDynamicProps: true,
        component: null,
      },
      {
        label: 'Video Avatar',
        icon: UserCircle,
        value: AGENT_TAB.AVATAR,
        group: 'Design',
        component: <AvatarSettings token={bot?.value?.accesstoken || ''} />,
      },
      {
        label: 'Pre-Chat Popup',
        icon: MousePointerClick,
        value: AGENT_TAB.PAGE_CTAS,
        group: 'Conversion',
        needsDynamicProps: true,
        component: null,
      },
      {
        label: 'Human Handoff',
        icon: Users,
        value: AGENT_TAB.HUMAN_TRANSFER,
        group: 'Conversion',
        component: <HumanTransfer token={bot?.value?.accesstoken || ''} />,
      },
      {
        label: 'Book Demo',
        icon: CalendarDays,
        value: AGENT_TAB.DEMO_BOOKING,
        group: 'Conversion',
        component: <DemoBooking token={bot?.value?.accesstoken || ''} />,
      },
      {
        label: 'Settings',
        icon: Settings,
        value: AGENT_TAB.SETTINGS,
        group: 'Configuration',
        component: (
          <Setting
            token={bot?.value?.accesstoken || ''}
            botName={bot?.label || ''}
            onDeleted={handleBotDeleted}
          />
        ),
      },
    ],
    [bot, handleBotDeleted],
  );

  const botSelectorActions = (
    <div className="flex items-center gap-2">
      <div className="flex items-center gap-1.5">
        <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide select-none">Bot</span>
        <FilterSelect
          value={selectedBotValue}
          onValueChange={handleBotSelect}
          options={options.map((opt, index): FilterSelectOption => ({
            value: String(index),
            label: opt.label,
          }))}
          placeholder="Select bot"
          className="h-9 w-[160px]"
        />
      </div>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setCreateModalOpen(true)}
        className="h-9 gap-1.5 border-blue-200 text-blue-600 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700 font-medium px-3 whitespace-nowrap"
      >
        <Plus className="w-3.5 h-3.5" />
        New Bot
      </Button>
    </div>
  );

  if (initializing) {
    return (
      <div className="flex flex-col h-screen w-full overflow-hidden bg-slate-50">
        <div className="flex-shrink-0">
          <PageHeader
            title="Chat Bot"
            description="Configure your AI chat agents and training data"
            icon={MessageSquare}
            actions={(
              <div className="flex items-center gap-2">
                <Skeleton className="h-9 w-[160px]" />
                <Skeleton className="h-9 w-[92px]" />
              </div>
            )}
            noReserveRight
          />
          {/* Tab bar skeleton */}
          <div className="px-4 py-3">
            <div className="flex border-b border-gray-200 gap-6 pb-3">
              {Array.from({ length: 7 }).map((_, i) => (
                <div key={i} className="flex items-center gap-1.5">
                  <Skeleton className="h-3.5 w-3.5 rounded" />
                  <Skeleton className="h-4 w-20" />
                </div>
              ))}
            </div>
          </div>
        </div>
        {/* Content card skeleton */}
        <div className="flex-1 min-h-0 px-4 pb-4 flex flex-col overflow-hidden">
          <div className="flex-1 min-h-0 flex flex-col bg-white overflow-hidden rounded-xl border border-slate-200 shadow-sm p-6">
            <div className="space-y-6 max-w-3xl w-full">
              <Skeleton className="h-6 w-48" />
              <Skeleton className="h-4 w-72" />
              <div className="space-y-5 pt-2">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="space-y-2">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-10 w-full" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (options.length === 0 && !bot) {
    return (
      <>
        <div className="flex flex-col h-screen overflow-hidden bg-slate-50">
          <div className="flex-shrink-0">
            <PageHeader
              title="Chat Bot"
              description="Configure your AI chat agents and training data"
              icon={MessageSquare}
              noReserveRight
            />
          </div>
          <div className="flex-1 flex items-center justify-center">
            <Button onClick={() => setCreateModalOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Create Your First Bot
            </Button>
          </div>
        </div>
        <NewBot
          open={createModalOpen}
          onOpenChange={setCreateModalOpen}
          onCreated={async (token) => {
            setCreateModalOpen(false);
            await loadBots(token);
          }}
        />
      </>
    );
  }

  return (
    <div className="flex flex-col h-screen w-full overflow-hidden bg-slate-50">
      <NewBot
        open={createModalOpen}
        onOpenChange={setCreateModalOpen}
        onCreated={async (token) => {
          setCreateModalOpen(false);
          await loadBots(token);
        }}
      />
      <div className="flex-shrink-0">
        <PageHeader
          title="Chat Bot"
          description="Configure your AI chat agents and training data"
          icon={MessageSquare}
          actions={botSelectorActions}
          noReserveRight
        />
        {/* Tab bar */}
        <div className="px-4 py-3">
          <div className="flex border-b border-gray-200 overflow-x-auto scrollbar-none">
            {AGENT_TABS.map((item, index) => {
              const Icon = item.icon;
              const isActive = activeTab === item.value;
              const startsNewGroup = index > 0 && AGENT_TABS[index - 1].group !== item.group;
              return (
                <Fragment key={item.value}>
                  {startsNewGroup && (
                    <span className="self-center mx-1 h-4 w-px bg-gray-200" aria-hidden="true" />
                  )}
                  <button
                    onClick={() => selectTab(item.value)}
                    title={item.group}
                    className={cn(
                      'flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap',
                      isActive
                        ? 'border-blue-600 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700',
                    )}
                  >
                    <Icon className="w-3.5 h-3.5" aria-hidden="true" />
                    {item.label}
                  </button>
                </Fragment>
              );
            })}
          </div>
        </div>
      </div>

      <div className="flex-1 min-h-0 px-4 pb-4 flex flex-col overflow-hidden">
        <div
          className={cn(
            'flex-1 min-h-0 flex flex-col bg-white overflow-hidden rounded-xl border border-slate-200 shadow-sm',
            !FILL_TABS.includes(activeTab as typeof FILL_TABS[number]) && 'overflow-y-auto',
          )}
        >
          <Tabs
            className={cn('w-full', FILL_TABS.includes(activeTab as typeof FILL_TABS[number]) && 'flex-1 min-h-0 flex flex-col')}
            value={activeTab}
            onValueChange={selectTab}
          >
            {AGENT_TABS.map((item) => {
              let content;
              if (item.value === AGENT_TAB.MESSAGES) {
                content = (
                  <Messages
                    token={bot?.value?.accesstoken || ''}
                  />
                );
              } else if (item.value === AGENT_TAB.CUSTOMIZE) {
                content = (
                  <Customize
                    token={bot?.value?.accesstoken || ''}
                    updateBotList={loadBots}
                    primaryColor={primaryColor}
                    setPrimaryColor={setPrimaryColor}
                  />
                );
              } else if (item.value === AGENT_TAB.PAGE_CTAS) {
                // Managed by FILL_TABS; component owns its own layout + scroll.
                content = <PageCTAs token={bot?.value?.accesstoken || ''} primaryColor={primaryColor} />;
              } else if (item.value === AGENT_TAB.KNOWLEDGE) {
                content = item.component;
              } else if (
                item.value === AGENT_TAB.PERSONA
                || item.value === AGENT_TAB.AVATAR
                || item.value === AGENT_TAB.HUMAN_TRANSFER
                || item.value === AGENT_TAB.DEMO_BOOKING
                || item.value === AGENT_TAB.SETTINGS
              ) {
                // These tabs manage their own padding + internal scroll (see FILL_TABS).
                content = item.component;
              } else {
                content = <div className="p-6">{item.component}</div>;
              }

              return (
                <TabsContent
                  className={cn(
                    item.value === AGENT_TAB.CUSTOMIZE && 'data-[state=inactive]:hidden',
                    'mt-0',
                    FILL_TABS.includes(item.value as typeof FILL_TABS[number]) && 'flex-1 min-h-0 flex flex-col',
                  )}
                  value={item.value}
                  key={item.value}
                  forceMount={item.value === AGENT_TAB.CUSTOMIZE ? true : undefined}
                >
                  {content}
                </TabsContent>
              );
            })}
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default CustomLivePage;
